/**
 * PerfilSection Component - Profile settings section
 */
import React from 'react';

interface PerfilSectionProps {
  formData: {
    full_name: string;
    email: string;
    telefone: string;
  };
  isLoadingProfile: boolean;
  isSavingProfile: boolean;
  profileError: string | null;
  profileSuccess: string | null;
  onInputChange: (field: string, value: string) => void;
  onSave: () => Promise<void>;
  getUserInitials: () => string;
}

// Validate email format
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone format (Brazilian)
const validatePhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 11;
};

export const PerfilSection: React.FC<PerfilSectionProps> = ({
  formData,
  isLoadingProfile,
  isSavingProfile,
  profileError,
  profileSuccess,
  onInputChange,
  onSave,
  getUserInitials,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Informações Pessoais</h3>

        {/* Avatar with dynamic initials */}
        <div className="flex items-center gap-6 mb-8">
          <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white text-3xl">
            {isLoadingProfile ? '...' : getUserInitials()}
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Avatar gerado automaticamente a partir do seu nome
            </p>
          </div>
        </div>

        {/* Loading state */}
        {isLoadingProfile && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-6">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Carregando dados do perfil...
            </p>
          </div>
        )}

        {/* Success message */}
        {profileSuccess && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-6">
            <p className="text-sm text-green-800 dark:text-green-300">
              {profileSuccess}
            </p>
          </div>
        )}

        {/* Error message */}
        {profileError && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-6">
            <p className="text-sm text-red-800 dark:text-red-300">
              {profileError}
            </p>
          </div>
        )}

        {/* Profile form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Nome Completo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => onInputChange('full_name', e.target.value)}
              disabled={isLoadingProfile || isSavingProfile}
              className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors
                ${profileError && !formData.full_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500'}
                ${(isLoadingProfile || isSavingProfile) ? 'opacity-50 cursor-not-allowed' : ''}`}
              placeholder="Digite seu nome completo"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              E-mail <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
              disabled={isLoadingProfile || isSavingProfile}
              className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors
                ${profileError && !validateEmail(formData.email) ? 'border-red-500' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500'}
                ${(isLoadingProfile || isSavingProfile) ? 'opacity-50 cursor-not-allowed' : ''}`}
              placeholder="seu.email@exemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Telefone
            </label>
            <input
              type="tel"
              value={formData.telefone}
              onChange={(e) => onInputChange('telefone', e.target.value)}
              disabled={isLoadingProfile || isSavingProfile}
              className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors
                ${profileError && formData.telefone && !validatePhone(formData.telefone) ? 'border-red-500' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500'}
                ${(isLoadingProfile || isSavingProfile) ? 'opacity-50 cursor-not-allowed' : ''}`}
              placeholder="(11) 98765-4321"
            />
          </div>
        </div>

        {/* Save button */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onSave}
            disabled={isLoadingProfile || isSavingProfile}
            className={`bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors
              ${(isLoadingProfile || isSavingProfile) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSavingProfile ? 'Salvando...' : 'Salvar Alterações'}
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            <span className="text-red-500">*</span> Campos obrigatórios
          </p>
        </div>
      </div>
    </div>
  );
};

export default PerfilSection;
